import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import alert from "components/elements/Alert";

import { generateRandomString } from "utils/helper";
import * as services from "../service/welfareApi";
import { setIsLoading, setWelfaleList } from "../slices/payrollSlice";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";

function toParams(params) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    ...params,
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      ?.filter?.((s) => s.order !== undefined)
      ?.map?.((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useWelfare() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const [, setMenu] = useState("welfare");
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const welfareList = useSelector((state) => state.payroll.welfareList || []);
  const [welfareOption, setWelfareOption] = useState([]);
  const [welfareSubTypeOptions, setWelfareSubTypeOptions] = useState([]);
  const [search, setSearch] = useState({});

  const getWelfareList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getWelfareList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(setWelfaleList(response?.data?.page?.content || []));
        setPagination({
          page: params.page + 1,
          size: params.size,
          range: [
            params.page * params.size + 1,
            Math.min((params.page + 1) * params.size, response?.data?.page?.totalElements),
          ],
          total: response?.data?.page?.totalElements,
        });
      } catch (error) {
        alert({ type: "error", resultObject: error });
      } finally {
        getWelfareType();
        getWelfareSubTypeOptions();
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );

  const getWelfareType = useCallback(
    async () => {
      try {
        const response = await services.getWelfareType({
          companyId: currentCompany,
          groupType: "WELFARE_TYPE"
        })
        dispatch(setWelfareOption(response?.data))
      } catch (error) {
      } finally {
        dispatch(setIsLoading(false))
      }
    }, [currentCompany, dispatch])

  const getWelfareSubTypeOptions = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          code: "WELFARE_SUBCATEGORY"
        };
        const response = await services.getWelfareSubType(values);
        setWelfareSubTypeOptions(response.data?.content);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch]
  );

  const modifiedWelfareOptions = useMemo(() => {
    const modifiedOptions = welfareOption?.map?.((opt) => ({
      ...opt,
      value: opt?.id,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
    })) || [];
    return modifiedOptions;
  }, [i18n.language, welfareOption]);

  const modifiedWelfareSubTypeOptions = useMemo(() => {
    const modifiedOptions = welfareSubTypeOptions.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, welfareSubTypeOptions]);

  const onCreate = useCallback(() => {
    navigate("/configuration/payroll/welfare/create");
  }, [navigate]);

  const handleMenuChange = useCallback(
    (e) => {
      setMenu(e);
    },
    [setMenu]
  );

  const onSubmit = useCallback(() => {
    navigate("/configuration/payroll", { state: { selectedTab: 'deduction' } });
    alert({ message: "Success" });
  }, [navigate]);


  const onSearch = useCallback(
    async (params = {}) => {
      setSearch(params)

      try {
        dispatch(setIsLoading(true));
        const response = await services.getWelfareList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          page: 0,
          size: 10,
        });

        dispatch(setWelfaleList(response?.data?.page?.content || []));
        setPagination({
          page: 1,
          size: 10,
          range: [
            1 * 10 + 1,
            Math.min((1 + 1) * 10, response?.data?.page?.totalElements),
          ],
          total: response?.data?.page?.totalElements,
        });
      } catch (error) {
        alert({ type: "error", resultObject: error });
      } finally {
        getWelfareType();
        getWelfareSubTypeOptions();
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration/payroll", { state: { selectedTab: 'deduction' } });
  }, [navigate]);

  const onEdit = useCallback(
    (record) => {
      if (record.id) {
        navigate(`/configuration/payroll/welfare/${record.id}/edit`);
      }
    },
    [navigate]
  );

  const onDelete = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          stpWelfareId: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
        };
        await services.manageStatusWelfare(values);
        alert({ message: "Success" });
        const modifiedParams = toParams({
          page: pagination.page - 1,
          size: pagination.size,
          sortBy: sorter,
        });
        getWelfareList(modifiedParams);
      } catch (error) {
        alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, sorter, dispatch, getWelfareList]
  );

  const onStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              stpWelfareId: record?.id,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: val ? "ACTIVE" : "INACTIVE",
            };
            await services.manageStatusWelfare(values);
            message.success(t("common.success"))

            const modifiedWelfareList = welfareList.map((i) => {
              if (record.id === i.id) {
                return { ...i, statusFlag: val ? "ACTIVE" : "INACTIVE" };
              }
              return i;
            });
            dispatch(setWelfaleList(modifiedWelfareList));

          } catch (error) {
            // alert({ type: "error", resultObject: error });
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })

    },
    [currentCompany, pagination, sorter, dispatch, welfareList]
  );

  const onChange = useCallback(
    (tablePagination, _, tableSorter) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);

      const modifiedParams = toParams({
        ...search,
        pagination: {
          page: tablePagination?.current - 1,
          pageSize: tablePagination?.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getWelfareList(modifiedParams);
    },
    [getWelfareList, search]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getWelfareList({
        page: 0,
        size: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    isLoading,
    pagination,
    welfareList,
    welfareOption,
    lang: i18n.language,
    welfareTypeOptions: modifiedWelfareOptions,
    welfareSubTypeOptions: modifiedWelfareSubTypeOptions,
    handleMenuChange,
    onCreate,
    onSubmit,
    onCancel,
    onSearch,
    onEdit,
    onDelete,
    onStatus,
    onChange,
  };
}

export default useWelfare;
